<template>
  <form ref="form">
    <v-container grid-list-lg class="dialog-form-full-width">
      <v-layout align-start justify-space-between nowrap row mt-0 pt-0>
        <v-flex xs12 md12 lg12>
          <v-select
            v-model="businessLineType"
            label="Type"
            v-validate="'required'"
            :items="typesItems"
            :error-messages="errors.collect('businessLineType')"
            data-vv-name="businessLineType"
            data-vv-as="Type"
            item-value="value"
            item-text="name"
          ></v-select>
        </v-flex>
      </v-layout>
      <v-layout align-start justify-space-between nowrap row mt-0 pt-0>
        <v-flex xs12 md12 lg12>
          <v-text-field
            v-model="name"
            label="Name"
            v-validate="'required'"
            :error-messages="errors.collect('name')"
            data-vv-name="name"
            data-vv-as="Name"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 md12 lg12>
          <v-text-field
            v-model="value"
            label="Value"
            v-validate="'required'"
            :error-messages="errors.collect('value')"
            data-vv-name="value"
            data-vv-as="Value"
          ></v-text-field>
        </v-flex>
        <v-flex>
          <v-checkbox
            v-model="isOem"
            label="OEM"
            hide-details
            data-vv-name="isOem"
            data-vv-as="OEM"
          ></v-checkbox>
        </v-flex>
      </v-layout>
      <v-layout align-start justify-space-between nowrap row mt-0 pt-0>
        <v-flex xs12 md12 lg12>
          <v-autocomplete
            v-model="customizations"
            clearable
            chips
            label="Configurations"
            :items="customizationsList"
            item-text="label"
            item-value="id"
            multiple
          ></v-autocomplete>
          <blockquote>
            Select relevant customizations for this entry. (Empty means
            <b>ALL</b>
            )
          </blockquote>
        </v-flex>
      </v-layout>
      <v-layout
        align-start
        justify-space-between
        nowrap
        row
        mt-0
        pt-0
        v-if="businessLineData.type !== 'businessLine'"
      >
        <v-flex xs12 md12 lg12>
          <v-autocomplete
            v-model="parents"
            clearable
            chips
            :label="parentType.name"
            :items="businessLinesList"
            item-text="name"
            item-value="id"
            multiple
          ></v-autocomplete>
          <blockquote>
            Set up {{ parentType.name.toLowerCase() }} for this entry, this way
            only selected {{ parentType.name.toLowerCase() }} will also include
            this entry. (Empty means
            <b>ALL</b>
            )
          </blockquote>
        </v-flex>
      </v-layout>
      <v-flex xs12>
        <v-btn @click="reset" class="ml-0" :disabled="!isChanged">
          {{ resetButtonText }}
        </v-btn>
        <v-btn
          :loading="loading"
          @click="submit"
          type="submit"
          color="primary"
          :disabled="!isChanged"
        >
          {{ saveButtonText }}
        </v-btn>
      </v-flex>
    </v-container>
  </form>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  mounted() {
    this.$store.dispatch("admin/fetchCustomizationList");

    if (this.isEdit) {
      this.businessLineData = { ...this.businessLine };
    }
  },
  $_veeValidate: {
    validator: "new"
  },

  props: {
    businessLine: {
      type: Object,
      default: () => ({
        id: undefined,
        name: undefined,
        type: undefined,
        value: undefined,
        isOem: undefined,
        customizations: [],
        parents: []
      })
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      businessLineData: {
        type: "businessLine"
      },
      typesItems: [
        {
          name: "1. Business Line",
          value: "businessLine"
        },
        {
          name: "2. Market Segment",
          value: "marketSegment"
        },
        {
          name: "3. Application",
          value: "application"
        }
      ],
      isChanged: false
    };
  },
  watch: {
    businessLine: {
      handler(val) {
        // Handle only edit mode
        if (val && val.name) {
          this.businessLineData = {
            ...val
          };
          // Handle add mode
        } else {
          this.businessLineData = {
            type: "businessLine"
          };
        }
      },
      deep: true
    },
    businessLineData: {
      handler(val) {
        this.isChanged =
          JSON.stringify(val) !== JSON.stringify(this.businessLine);
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters("admin", {
      customizationsList: "getCustomizationList",
      businessLines: "getBusinessLinesList"
    }),
    isEdit() {
      return this.businessLine && this.businessLine.name;
    },
    saveButtonText() {
      return this.isEdit ? "Update" : "Create";
    },
    resetButtonText() {
      return this.isEdit ? "Reset" : "Clear";
    },
    parentType() {
      switch (this.businessLineType) {
        case "application":
          return { name: "Market Segments", type: "marketSegment" };
        default:
          return { name: "Business Lines", type: "businessLine" };
      }
    },
    businessLinesList() {
      const filterType = this.parentType.type;

      return this.businessLines
        .filter(item => item.type === filterType)
        .map(item => {
          return {
            id: item.id,
            name: item.name
          };
        });
    },
    businessLineType: {
      get() {
        return this.businessLineData.type;
      },
      set(value) {
        this.$set(this.businessLineData, "type", value);
      }
    },
    name: {
      get() {
        return this.businessLineData.name;
      },
      set(value) {
        this.$set(this.businessLineData, "name", value);
      }
    },
    value: {
      get() {
        return this.businessLineData.value;
      },
      set(value) {
        this.$set(this.businessLineData, "value", value);
      }
    },
    isOem: {
      get() {
        return this.businessLineData.isOem || false;
      },
      set(value) {
        this.$set(this.businessLineData, "isOem", value);
      }
    },
    customizations: {
      get() {
        return this.businessLineData.customizations;
      },
      set(value) {
        this.$set(this.businessLineData, "customizations", value);
      }
    },
    parents: {
      get() {
        return this.businessLineData.parents;
      },
      set(value) {
        this.$set(this.businessLineData, "parents", value);
      }
    }
  },
  methods: {
    reset() {
      if (this.isEdit) {
        this.businessLineData = {
          ...this.businessLine
        };
        this.$validator.reset();

        return;
      }

      this.$refs.form.reset();
      this.$validator.reset();
      this.businessLineData = {
        type: "businessLine"
      };
    },
    submit(event) {
      event.preventDefault();
      this.$validator.validate().then(valid => {
        if (valid) {
          this.$emit("save", {
            businessLine: this.businessLineData
          });
        }
      });
    }
  }
};
</script>
